/**
 * @module app.core
 */

import { autoinject, bindable } from 'aurelia-framework';
import { ComponentBase, IForm, SortDirection, Sorting } from 'app/shared/lib/essette-site';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { SaveAutomation } from 'app/core/components/automations/save-automation';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { convertStringToProperCase, deepClone } from 'app/shared/lib/utilities';
import { validate } from 'app/shared/elements/ess-rule';
import { AutomationEngineService } from 'app/ae/services/automation-engine-service';
import { localSort } from 'app/shared/lib/sorting';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { I18N } from 'aurelia-i18n';
import * as AE from 'app/ae/models/essette-api';

export enum EventOptions {
    Insert = 'Insert',
    Update = 'Update'
}

export type EventOption = EventOptions.Insert | EventOptions.Update;

export interface RuleConditionOption {
    /** When true, condition option does not appear in the Property dropdown */
    $used: boolean;
    /** The translated I18N resource that represents the condition option in the Property dropdown */
    translatedDisplay: string;
    /** String expression that represents selections for a given Property and is readable by Raven */
    expression: string;
    /** This condition option's stored expression converted into an object format for ease of use */
    parsedExpression: any;
    /** The options (checkboxes) to appear at the parent level */
    options: {
        $selected: boolean;
        $expanded: boolean;
        [key: string]: any;
    }[];
    /** The raw I18N resource used in translatedDisplay */
    displayKey: string;
    /** The raw I18N resource that represents the options (checkboxes) at the parent level */
    optionKey: string;
    /** The highest-level property for the option (i.e. "acuity" in "acuity.id") */
    optionLevel1: string;
    /** The second-highest-level property for the option (i.e. "id" in "acuity.id") */
    optionLevel2: string;
    /** The raw I18N resource that represents the options (checkboxes) at the child level */
    subOptionKey?: string;
    /** The property name containing the included child options (i.e. "subclasses," "programs", etc.) */
    subOptionProperty?: string;
    /** The highest-level property for a child option (i.e. "program" in "program.id") */
    subOptionLevel1?: string;
    /** The second-highest level property for a child option (i.e. "id" in "program.id") */
    subOptionLevel2?: string;
    /** If set, do not allow condition option to be selected when event matches */
    excludeOnEvent?: EventOption;
}

export interface RuleActionOption {
    /** The translated I18N resource that represents the action option in the Type dropdown */
    translatedDisplay: string;
    /** The options to appear in the dropdown */
    options: {
        [key: string]: any;
    }[];
    selectedOption: any;
    /** The raw I18N resource that represents the options in the dropdown */
    optionKey: string;
    /** The highest-level property for the payload (i.e. "queues") */
    payloadLevel1: string;
    /** The second-highest-level property for the payload (i.e. "code") */
    payloadLevel2: string;
    /** The Raven-appropriate string dictating which action to initiate upon success (i.e., "CM.Case.Route") */
    successActionType: string;
}

interface IFilter {
    isDraft: Array<boolean>
};

export interface CustomRule extends AE.Rule {
    event?: string | null;
    ruleSetIdentifier?: string | null;
    hasWarnings?: boolean;
    warnings?: string[];
    actionText?: string | null;
    propertiesDisplay?: string | null;
    propertiesLinkText?: string | null;
    propertiesTooltip?: string | null;
}

@autoinject()
export class AutomationsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected automations: ReadonlyArray<CustomRule> = [];
    private automationsToDisplay: Array<CustomRule> = [];
    protected editedAutomation?: CustomRule;

    @bindable
    protected moduleCode: string | null = null;

    @bindable
    protected recordTypes: string[] = [];

    @bindable
    protected actions: RuleActionOption[] = [];

    @bindable
    protected options: RuleConditionOption[] = [];

    private sorting = new Sorting();
    protected includeInactive = false;
    protected isValid: boolean = false;
    protected isAdd: boolean = false;
    protected validate = validate;
    protected pageSize = 25;
    protected resultCount = 0;

    private filterOptions: IFilter = {
        isDraft: [],
    };

    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $form!: IForm;
    protected $saveAutomation!: SaveAutomation;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: AutomationEngineService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.moduleCode) throw new Error('ModuleCode not bound.');
        this.filterOptions.isDraft = [false];
        this.reload();
    }

    ////****************** Events ***************************************************////
    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }

        this.getAutomationsToDisplay();
        //Call stop Propagation to prevent parent table from reload and losing sort
        event.stopPropagation();
    }


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadAutomations();
    }

    protected saveAutomation(automation: AE.Rule = {}): void {
        if(automation.identifier) {
            const clone: any = deepClone(automation);
            // Remove UI-only fields so save payload doesn't include extraneous properties
            delete clone.recordType;
            delete clone.actionText;
            delete clone.actionSortKey;
            delete clone.propertiesDisplay;
            delete clone.propertiesLinkText;
            delete clone.propertiesTooltip;
            delete clone.propertiesSortKey;
            delete clone.warnings;
            delete clone.hasWarnings;
            this.editedAutomation = clone;
        } else {
            this.editedAutomation = {
                description: '',
                identifier: '',
                expression: '',
                event: null
            };
        }
        this.isAdd = !automation?.identifier;
        this.$modal.open();
    }

    protected async handleSaveAutomationFormSuccess(): Promise<void> {
        if(this.$form.reset) this.$form.reset();

        try {
            (this.options || []).forEach((opt: any) => {
                opt.expression = '';
                opt.parsedExpression = null;
                opt.$used = false;
                if(Array.isArray(opt.options)) {
                    opt.options.forEach((o: any) => {
                        o.$selected = false;
                        o.$expanded = false;
                        if(opt.subOptionProperty && Array.isArray(o[opt.subOptionProperty])) {
                            o[opt.subOptionProperty].forEach((so: any) => so.$selected = false);
                        }
                    });
                }
            });
        } catch { /* ignore any runtime cleanup errors */ }

        this.$modal.close();

        try {
            const evt = new CustomEvent('automations-saved', { bubbles: true, composed: true });
            (this as any).element?.dispatchEvent ? (this as any).element.dispatchEvent(evt) : (document.dispatchEvent(evt));
        } catch { /* ignore event dispatch errors */ }

        await this.reload();
    }

    protected async handleSaveAutomationModalClose(): Promise<void> {
        if(this.$form.hasChanges()) {
            const result = await confirmation.showCancelChanges('ae:automation.$');
            if(result.dialogResponse !== confirmation.options.OK) return;
        }
        if(this.$form.reset) this.$form.reset();

        try {
            (this.options || []).forEach((opt: any) => {
                opt.expression = '';
                opt.parsedExpression = null;
                opt.$used = false;
                if(Array.isArray(opt.options)) {
                    opt.options.forEach((o: any) => {
                        o.$selected = false;
                        o.$expanded = false;
                        if(opt.subOptionProperty && Array.isArray(o[opt.subOptionProperty])) {
                            o[opt.subOptionProperty].forEach((so: any) => so.$selected = false);
                        }
                    });
                }
            });
        } catch { /* ignore cleanup errors */ }

        this.$modal.close();
    }

    private toggleActiveState(): void {
        if(this.includeInactive) {
            this.filterOptions.isDraft = [true, false];
        } else {
            this.filterOptions.isDraft = [false];
        }

        this.getAutomationsToDisplay();
    }

    private getAutomationsToDisplay() {
        // Apply filter
        this.automationsToDisplay = this.automations.filter((automation) => this.filterOptions.isDraft.includes(automation.isDraft as boolean));
        const defaultSort = new Sorting();
        defaultSort.add({
            reference: 'description',
            direction: SortDirection.Ascending
        });
        localSort(this.automationsToDisplay, this.sorting.entries.length > 0 ? this.sorting : defaultSort);
        // move automations with warnings to the top
        const automationsWithWarnings = this.automationsToDisplay.filter(a => a.hasWarnings);
        const automationsWithoutWarnings = this.automationsToDisplay.filter(a => !a.hasWarnings);
        this.automationsToDisplay = [...automationsWithWarnings, ...automationsWithoutWarnings];

        // local pagination
        const currentPage = this.$pager?.page || 0;
        this.resultCount = this.automationsToDisplay.length;
        this.automationsToDisplay = this.automationsToDisplay.slice(currentPage * this.pageSize, currentPage * this.pageSize + this.pageSize);
    }

    /**
     * Determine the record type label for the current module.
     * Currently maps known module prefixes to the required, case-sensitive
     * display values. Since each module currently only targets one record
     * type, this is derived from the bound `moduleCode`.
     */
    private getRecordTypeFromModule(): string | null {
        const code = (this.moduleCode || '').toLowerCase();
        if(!code) return null;

        // Known mappings (case-sensitive display required)
        if(code.startsWith('cm')) return this.i18n.tr('cm:case.$');
        if(code.startsWith('um')) return this.i18n.tr('um:auth.$');

        // Fallback: attempt to convert moduleCode to a proper-case token
        // (keeps behavior safe if new modules appear). This will not
        // invent 'Case'/'Auth' if unknown; it simply returns a readable
        // fallback string.
        try {
            return convertStringToProperCase(this.moduleCode || '');
        } catch {
            return this.moduleCode || null;
        }
    }

    /**
     * Detect whether the provided option appears in the given Raven expression string
     * using the same patterns that `SaveAutomation.generateParsedExpression` uses.
     */
    private optionInExpression(opt: RuleConditionOption, exprStr: string | null): boolean {
        if(!exprStr) return false;
        const optionLevel1 = (opt as any).optionLevel1 || '';
        const optionLevel2 = (opt as any).optionLevel2 || '';
        const subOptionProperty = (opt as any).subOptionProperty;
        const subOptionLevel1 = (opt as any).subOptionLevel1 || '';
        const subOptionLevel2 = (opt as any).subOptionLevel2 || '';
        if(!optionLevel1 || !optionLevel2) return false;

        try {
            if(!subOptionProperty) {
                const re = new RegExp(`\\(new\\[\\] \\{[^}]+\\}\\.Any\\(x => root\\.${optionLevel1}\\.${optionLevel2} == x\\)\\)`);
                return !!exprStr.match(re);
            } else {
                const re = new RegExp(`\\(${optionLevel1}\\.${optionLevel2} eq \\\"[^\\\"]+\\\" && new\\[\\] \\{[^}]+\\}\\.Any\\(x => root\\.${subOptionLevel1}\\.${subOptionLevel2} == x\\)\\)`, 'g');
                return !!exprStr.match(re);
            }
        } catch {
            return false;
        }
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadAutomations(): Promise<void> {
        try {
            this.state.beginLoad();
            const response = await this.service.loadAllRuleSets();
            this.automations = [];
            const ruleSets = response.results.filter(({ identifier }) => this.moduleCode && identifier?.toLocaleLowerCase().startsWith(this.moduleCode.toLocaleLowerCase()));
            ruleSets.forEach(ruleSet => {
                this.automations = this.automations.concat(
                    (ruleSet.rules || []).map((rule) => {
                        const warnings: string[] = [];
                        // for each success action
                        const actionItems: string[] = [];
                        const actionSortItems: string[] = [];
                        rule.successActions?.forEach(sa => {
                            // find its corresponding action option
                            const foundSuccessAction = this.actions.find(a => a.successActionType === sa.type);
                            if(!foundSuccessAction) return;
                            // extract the data needed from the found action option
                            const { payloadLevel1, payloadLevel2, options, optionKey } = foundSuccessAction;
                            // including the action option's payload level1 (i.e. "queues" associated with that action option)
                            const foundPayloadLevel1 = sa.payload[payloadLevel1];
                            if(!foundPayloadLevel1 || !foundPayloadLevel1.length) return;
                            // for each payload level 1 (i.e., for each queue associated with the success action)
                            foundPayloadLevel1.forEach((payloadLevel1: any) => {
                                const foundPayloadLevel2 = payloadLevel1[payloadLevel2];
                                // Queue Code is empty/missing
                                if(!foundPayloadLevel2) {
                                    const what = `${this.i18n.tr(optionKey)} ${payloadLevel2}`;
                                    const warning = this.i18n.tr('ui:messages.warnings.missing', { what });
                                    warnings.push(warning);
                                    return;
                                }
                                // find its corresponding record based on payload level 2 (i.e., "code")
                                const foundPayloadLevel2Value = options.find(o => o[payloadLevel2] === foundPayloadLevel2 && foundPayloadLevel2 !== null);
                                let what = `${this.i18n.tr(optionKey)}`;
                                let warning: string | null = null;
                                if(!foundPayloadLevel2Value) {
                                    // if the selected queue is not found, warn the user
                                    warning = this.i18n.tr('ui:messages.warnings.notFound', { what });
                                } else if(!foundPayloadLevel2Value.isActive) {
                                    // and if the found queue is inactive, warn the user
                                    if(foundPayloadLevel2Value.description) what += ` ${foundPayloadLevel2Value.description}`;
                                    warning = this.i18n.tr('ui:messages.warnings.inactive', { what });
                                }
                                if(warning) {
                                    warnings.push(warning);
                                } else if(foundPayloadLevel2Value) {
                                    // Build an action display and a sortable composite key.
                                    // Build a deterministic action display to avoid duplicated "Queue" fragments
                                    const recordTypeDisplay = this.getRecordTypeFromModule() || '';
                                    const queueDesc = (foundPayloadLevel2Value.description || '').toString();
                                    // Per spec, always display as: "Route to {Case/Auth} Queue: {QueueDescription}"
                                    const queueLabel = this.i18n.tr('core:queue.$');
                                    const what = `${recordTypeDisplay} ${queueLabel}: ${queueDesc}`.trim();
                                    const actionDisplay = this.i18n.tr('ae:local.routeTo', { what });
                                    actionItems.push(actionDisplay);
                                    // Determine an action type token for sorting: prefer last segment of successActionType (e.g., 'Route'),
                                    // otherwise fall back to translated display.
                                    const actionTypeToken = (foundSuccessAction.successActionType || '').toString().split('.').slice(-1)[0] || (foundSuccessAction.translatedDisplay || '');
                                    const sortKey = `${actionTypeToken.toString().toLowerCase()}|${queueDesc.toString().toLowerCase()}`;
                                    actionSortItems.push(sortKey);
                                }
                            });
                        });

                        // Ensure the values used for sorting are plain strings (not null/undefined/arrays)
                        const recordTypeString = this.getRecordTypeFromModule() || '';
                        const actionTextString = actionItems.length ? actionItems.join('\n') : '';
                        const actionSortKeyString = actionSortItems.length ? actionSortItems.join('||') : '';

                        // Determine configured properties for the rule using the passed-in `options`.
                        // We'll compare option keys (optionLevel1 or displayKey) so that we
                        // correctly detect which top-level properties are used and avoid
                        // false positives when display strings duplicate or child options exist.
                        const rulePropertyKeys = new Set<string>();
                        const displayByKey: Record<string, string> = {};
                        try {
                            const parsed = (rule as any).parsedExpression || null;
                            const exprStr = typeof (rule as any).expression === 'string' ? (rule as any).expression : (parsed ? JSON.stringify(parsed) : '');

                            // Build available option keys (top-level) from options used for counting
                            const availableOptionsList = (this.options || []).filter(o => !(o as any).$used);
                            const availableOptionKeys = new Set<string>(availableOptionsList.map(o => ((o.optionLevel1 || o.displayKey) || '').toString()));

                            // Iterate available condition options and detect which are referenced by the rule
                            availableOptionsList.forEach(opt => {
                                try {
                                    const key = ((opt as any).optionLevel1 || (opt as any).displayKey || '').toString();
                                    const display = (opt as any).translatedDisplay || (opt as any).displayKey || key || '';
                                    const level1 = (opt as any).optionLevel1 || '';
                                    const level2 = (opt as any).optionLevel2 || '';
                                    // Fast path: if server provided a parsedExpression object, prefer it (more reliable than regex)
                                    try {
                                        if(parsed && typeof parsed === 'object') {
                                            if(level1 && level2 && parsed.hasOwnProperty(level1) && parsed[level1] && parsed[level1].hasOwnProperty(level2)) {
                                                rulePropertyKeys.add(key);
                                                displayByKey[key] = display;
                                                return; // next option
                                            }
                                        }
                                    } catch { /* ignore parsed checks */ }

                                    // Fallback: exact detection using the same patterns used when saving rules.
                                    try {
                                        if(this.optionInExpression(opt, exprStr)) {
                                            rulePropertyKeys.add(key);
                                            displayByKey[key] = display;
                                        }
                                    } catch { /* ignore regex errors */ }
                                } catch { /* ignore individual option parse errors */ }
                            });

                            // Convert keys to display values and sort A-Z
                        } catch {
                            // If anything goes wrong, leave rulePropertyKeys empty and let UI show nothing
                        }

                        const normalizedProps = Array.from(rulePropertyKeys).map(k => displayByKey[k] || k).filter(Boolean).sort((a, b) => a.localeCompare(b));
                        const availableOptionKeysFinal = new Set((this.options || []).filter(o => !(o as any).$used).map(o => ((o.optionLevel1 || o.displayKey) || '').toString()));

                        // Build display values per requirements
                        let propertiesDisplay = '';
                        let propertiesLinkText: string | null = null;
                        // Only count options that are actually available for selection ($used indicates the option should not appear in the Property dropdown).
                        const totalAvailable = availableOptionKeysFinal.size || ((this.options || []).length);

                        if(normalizedProps.length === 1) {
                            propertiesDisplay = normalizedProps[0];
                        } else if(normalizedProps.length > 1 && normalizedProps.length < totalAvailable) {
                            propertiesLinkText = `${normalizedProps.length} ${this.i18n.tr('ae:automation.property.$', { count: 2 })}`;
                        } else if(normalizedProps.length === totalAvailable && totalAvailable > 0) {
                            propertiesLinkText = this.i18n.tr('ui:labels.all', { what: this.i18n.tr('ae:automation.property.$', { count: 2 }) });
                        }

                        let propertiesTooltip: any = '';
                        if(normalizedProps.length) {
                            const items = normalizedProps.map(p => `<li>${p}</li>`).join('');
                            propertiesTooltip = `<ul>${items}</ul>`;
                        }

                        return {
                            ...rule,
                            ruleSetIdentifier: ruleSet.identifier,
                            // recordType must be a primitive string for sorting to work consistently
                            recordType: recordTypeString,
                            // actionText: human readable multiline string shown in UI (always a string)
                            actionText: actionTextString,
                            // actionSortKey: composite key that sorts by action type then description (always a string)
                            actionSortKey: actionSortKeyString,
                            event: convertStringToProperCase((ruleSet.identifier || '').split('.').slice(-1)[0]),
                            warnings,
                            hasWarnings: !!warnings.length,
                            // Properties UI fields
                            propertiesDisplay,
                            propertiesLinkText,
                            propertiesTooltip
                        };
                    })
                )
            });
            this.getAutomationsToDisplay();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateAutomation(automation: CustomRule) {
        if(!automation.ruleSetIdentifier) return;

        // Show confirmation dialog only if deactivating
        if(!automation.isDraft) {
            const result = await confirmation.showDeactivate('ae:automation.$', 1);
            if(result.dialogResponse !== confirmation.options.OK) return;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            automation.isDraft = !automation.isDraft;
            const { event, ruleSetIdentifier, hasWarnings, warnings, ...updatedAutomation } = automation;
            const response = await this.service.saveRuleSet({
                identifier: ruleSetIdentifier,
                rules: [
                    // remove UI-only fields before sending
                    ((): any => {
                        const payload = { ...updatedAutomation } as any;
                        delete payload.recordType;
                        delete payload.actionText;
                        delete payload.actionSortKey;
                        delete payload.propertiesDisplay;
                        delete payload.propertiesLinkText;
                        delete payload.propertiesTooltip;
                        delete payload.propertiesSortKey;
                        delete payload.warnings;
                        delete payload.hasWarnings;
                        return payload;
                    })()
                ]
            });
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.activate('ae:automation.$', !automation.isDraft as boolean, isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    protected async deleteAutomation(automation: CustomRule) {
        if(!automation.ruleSetIdentifier || !automation.identifier) return;

        const result = await confirmation.showDelete('ae:automation.$', 1);
        if(result.dialogResponse !== confirmation.options.OK) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const { ruleSetIdentifier, identifier } = automation;
            const response = await this.service.saveRuleSet({
                identifier: ruleSetIdentifier,
                rules: [
                    {
                        identifier,
                        doDelete: true
                    }
                ]
            });
            isSuccessful = response.isSuccessful;
            await this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.deleted('ae:automation.$', isSuccessful);
            this.state.endSave();
        }
    }
}
